import { Component, Input, OnInit } from '@angular/core';
import { FormControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { AuthStoreService, DialogNgbService, UserSettings } from '@motivforce/mx-library-angular';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter } from 'rxjs';
import { take } from 'rxjs/operators';
import { FieldGroup } from 'src/app/core/model/core/field-group';
import { PaymentsDetails } from 'src/app/core/model/lenovo/payments-details';
import { UserStoreService } from 'src/app/core/store/user-store.service';

import { FieldDefinition } from '../../../core/model/core/field-definition';
import { User } from '../../../core/model/user';

@UntilDestroy()
@Component({
  selector: 'app-bank-details-form',
  templateUrl: './bank-details-form.component.html',
  styleUrls: ['./bank-details-form.component.scss'],
})
export class BankDetailsFormComponent implements OnInit {
  @Input() readonly = false;

  details: PaymentsDetails;
  formFields: FieldDefinition[];

  form: UntypedFormGroup;

  formIsDisabled = true;

  customLengthFields = ['Country', 'State', 'City', 'Postcode', 'Beneficiary City', 'Beneficiary Country'];

  constructor(
    private userStore: UserStoreService,
    private formBuilder: UntypedFormBuilder,
    private authStore: AuthStoreService,
    private dialog: DialogNgbService
  ) {}

  ngOnInit(): void {
    this.userStore.paymentsDetailsResult$.pipe(untilDestroyed(this), filter(Boolean)).subscribe((paymentsDetails) => {
      this.formIsDisabled = true;

      const rawFields = paymentsDetails.fields as any[];

      const groupsMap = new Map<string, { sortOrder: number; label: string; fields: FieldDefinition[] }>();

      rawFields.forEach((field: any) => {
        const groupName = field.fieldGroup;

        if (!groupsMap.has(groupName)) {
          groupsMap.set(groupName, {
            sortOrder: field.fieldGroupSortOrder,
            label: field.fieldGroupLabel,
            fields: [],
          });
        }

        groupsMap.get(groupName)!.fields.push(field);
      });

      const groupedFields: FieldGroup[] = Array.from(groupsMap.entries())
        .sort((a, b) => a[1].sortOrder - b[1].sortOrder)
        .map(([groupName, groupData]) => ({
          name: groupName,
          label: groupData.label,
          fields: groupData.fields.sort((fieldA, fieldB) => fieldA.sortOrder - fieldB.sortOrder),
        }));

      this.details = {
        ...paymentsDetails,
        fields: groupedFields,
      };

      this.formFields = groupedFields.flatMap((group) => group.fields);

      this.form = this.formBuilder.group(
        this.formFields.reduce((formFields: { [x: string]: any }, curr) => {
          const validators = [];

          if (!curr.skipClientSideValidation) {
            if (curr.isRequired) validators.push(Validators.required);
            if (curr.validationRegEx) validators.push(Validators.pattern(curr.validationRegEx));
          }

          formFields[this.toCamelCase(curr.fieldName)] = new FormControl(
            { value: curr.value || '', disabled: !curr.isEditable || this.formIsDisabled },
            validators
          );

          if (curr.options) {
            curr.filteredOptions = [...curr.options];
          }

          return formFields;
        }, {})
      );

      this.form.patchValue(this.details);
      this.preSelectUserCountry();
    });

    this.authStore.isRegularUser$.pipe(untilDestroyed(this), filter(Boolean)).subscribe(() => {
      this.userStore.getPaymentsDetails();

      const { user } = this.authStore.userSettings as UserSettings<User>;
      const isAp = user?.region?.regionCode === 'AP';

      if (user != null) {
        if (!this.userStore.wasBankDetailsDialogShown && user.isBoostPaymentDetailsSetUp === false && isAp) {
          this.dialog.openFeatureDialog('bankDetailsDialog', [], { size: 'md' });
          this.userStore.wasBankDetailsDialogShown = true;
        }
      }
    });
  }

  toCamelCase(s: string): string {
    if (s === s.toUpperCase()) {
      return s.toLowerCase();
    }
    return s.charAt(0).toLowerCase() + s.slice(1);
  }

  enableForm(): void {
    this.formIsDisabled = false;
    this.form.enable();
    this.form.get('country')?.disable();
  }

  onSubmit(): void {
    this.form.markAllAsTouched();
    if (this.form.valid) {
      this.userStore.updatePaymentDetails({ ...this.form.getRawValue() } as PaymentsDetails);
    }
  }

  preSelectUserCountry(): void {
    this.authStore.userSettings$.pipe(untilDestroyed(this), filter(Boolean), take(1)).subscribe((userSettings) => {
      if (userSettings.user != null) {
        const { country } = userSettings;
        if (country) {
          this.form.get('country')?.patchValue(country.alpha2Code);
        }
      }
    });
  }
}
